/**
 * cm-adaptive-gallery.js
 *
 * Galeriehöhe folgt dem aktiven Slide (Flickity-Prinzip `adaptiveHeight`).
 *
 * Warum das hier anders gebaut ist als in Flickity-basierten Themes:
 * Flickity-Galerien schieben die Bilder per CSS-Transform. Eine
 * Höhenänderung kann dort nichts stören. Horizon nutzt natives CSS-Scroll-Snap.
 * Ändert man die Höhe des Scroll-Containers während einer Bewegung, bricht der
 * Browser das Snapping ab und der Slide bleibt auf halber Strecke stehen.
 *
 * Deshalb wird hier NICHT der Scroll-Container angefasst:
 *   <slideshow-slides>    = Scroll-Container, konstante Höhe (höchstes Bild)
 *   <slideshow-container> = Sichtfenster, dessen Höhe wir animieren
 *
 * Da das Sichtfenster nicht scrollt, darf seine Höhe pro Frame mitlaufen.
 * Ergebnis: die Galerie wächst und schrumpft synchron zur Wischbewegung,
 * ohne die Scroll-Physik überhaupt zu berühren.
 */

const GALLERY_SELECTOR = 'media-gallery.cm-adaptive-gallery';
const HEIGHT_VAR = '--cm-gallery-h';
const TALLEST_VAR = '--cm-tallest-h';
const READY_ATTR = 'cm-ready';

class AdaptiveGallery {
  #gallery;
  #slideshow;
  #viewport;
  #scroller;
  #slides = [];
  #heights = [];
  #centers = [];
  #lastWidth = 0;
  #lastHeight = 0;
  #frame = null;
  #resizeObserver;
  #mutationObserver;

  /** @param {HTMLElement} gallery */
  constructor(gallery) {
    this.#gallery = gallery;
    this.#slideshow = gallery.querySelector('slideshow-component');
    this.#viewport = this.#slideshow?.querySelector('slideshow-container') ?? null;
    this.#scroller = this.#slideshow?.querySelector('slideshow-slides') ?? null;

    if (!this.#viewport || !this.#scroller) return;

    this.#measure();
    this.#render();

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    gallery.addEventListener('slideshow:select', this.#onScroll);

    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this.#slideshow);

    this.#mutationObserver = new MutationObserver(() => {
      this.#measure();
      this.#render();
    });
    this.#mutationObserver.observe(this.#scroller, { childList: true });

    this.#gallery.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener(
          'load',
          () => {
            this.#measure();
            this.#render();
          },
          { once: true }
        );
      }
    });

    gallery.cmAdaptiveGallery = this;
  }

  /**
   * Seitenverhältnis (Breite/Höhe) eines Slides.
   * @param {Element | undefined} slide
   * @returns {number | null}
   */
  #ratioOf(slide) {
    if (!slide) return null;

    const media = slide.querySelector('.product-media');
    if (media) {
      const value = parseFloat(getComputedStyle(media).getPropertyValue('--ratio'));
      if (Number.isFinite(value) && value > 0) return value;
    }

    const img = slide.querySelector('img');
    if (img?.naturalWidth && img?.naturalHeight) return img.naturalWidth / img.naturalHeight;

    return null;
  }

  /**
   * Zielhöhen und Slide-Mitten einmalig vermessen und die konstante Höhe des
   * Scroll-Containers festlegen. Nur bei Resize oder neuen Slides nötig.
   */
  #measure() {
    this.#slides = Array.from(this.#scroller.querySelectorAll(':scope > slideshow-slide'));
    if (!this.#slides.length) return;

    const width = this.#viewport.clientWidth || this.#gallery.clientWidth;

    this.#heights = this.#slides.map((slide) => {
      const ratio = this.#ratioOf(slide) ?? 1;
      return width ? width / ratio : 0;
    });

    this.#centers = this.#slides.map((slide) => slide.offsetLeft + slide.offsetWidth / 2);

    // Scroll-Container so hoch wie das höchste Bild – dieser Wert ändert sich
    // während des Wischens nie, deshalb bleibt das Snapping unangetastet.
    const tallest = Math.max(...this.#heights, 0);
    if (tallest) this.#gallery.style.setProperty(TALLEST_VAR, `${Math.round(tallest)}px`);
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#render();
    });
  };

  #onResize = (entries) => {
    const width = entries?.[0]?.contentRect?.width ?? 0;
    if (Math.abs(width - this.#lastWidth) < 1) return;
    this.#lastWidth = width;
    this.#measure();
    this.#render();
  };

  /**
   * Höhe des Sichtfensters für die aktuelle Scrollposition setzen.
   * Zwischen zwei Slides wird linear interpoliert, die Galerie folgt der
   * Wischbewegung also 1:1 statt nachträglich zu animieren.
   */
  #render() {
    if (!this.#slides.length) return;

    const centers = this.#centers;
    const heights = this.#heights;
    const position = this.#scroller.scrollLeft + this.#scroller.clientWidth / 2;

    let height;

    if (position <= centers[0]) {
      height = heights[0];
    } else if (position >= centers[centers.length - 1]) {
      height = heights[heights.length - 1];
    } else {
      height = heights[0];

      for (let index = 0; index < centers.length - 1; index++) {
        const start = centers[index];
        const end = centers[index + 1];
        if (position < start || position > end || end === start) continue;

        const progress = (position - start) / (end - start);
        height = heights[index] + (heights[index + 1] - heights[index]) * progress;
        break;
      }
    }

    height = Math.round(height);
    if (!height || height === this.#lastHeight) return;
    this.#lastHeight = height;

    this.#gallery.style.setProperty(HEIGHT_VAR, `${height}px`);
    this.#gallery.setAttribute(READY_ATTR, '');
  }

  destroy() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#gallery.removeEventListener('slideshow:select', this.#onScroll);
    this.#gallery.removeAttribute(READY_ATTR);
    this.#gallery.style.removeProperty(HEIGHT_VAR);
    this.#gallery.style.removeProperty(TALLEST_VAR);
  }
}

const init = (root = document) => {
  root.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
    if (gallery.cmAdaptiveGallery) return;
    new AdaptiveGallery(gallery);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => init(event.target ?? document));
document.addEventListener('shopify:block:select', () => init());

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.(GALLERY_SELECTOR)) init(node.parentElement ?? document);
      else if (node.querySelector?.(GALLERY_SELECTOR)) init(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
